/**
 * 세션 146 — User-Agent 기반 봇 분류.
 * middleware.ts 수정 금지 (CSP 충돌) — page_views INSERT 지점에서 직접 호출.
 *
 * [S10-1] 정본 규약: 사람 = bot_type === 'human'. 빈 문자열/NULL 을 사람으로 판정하는 코드·뷰를 새로 만들지 말 것.
 *   이 함수는 사람에게도 반드시 문자열 'human' 을 기록한다 (아래 두 return 지점).
 *   어드민 뷰가 COALESCE(bot_type,'')='' 를 사람으로 보다가 7일 사람 PV 544건을 0으로 표시한 사고가 있었다.
 */
export type BotType = 'yeti' | 'googlebot' | 'bingbot' | 'naver' | 'daum' | 'other' | 'human';

// ADM-H(2026-09-25) — 렌더 경로(page/layout/components)는 이 리터럴을 직접 들고 있으면
// 안 된다(src/__tests__/no-ua-cloaking.test.ts 세 번째 검사, BOT_RE). bot_hits 조회처럼
// «분류값으로 이미 적재된 값을 필터링」하는 건 UA 클로킹이 아니지만, 정규식은 문자열만 본다 —
// 이 상수를 통해서만 참조해 렌더 파일 소스에 리터럴이 남지 않게 한다.
export const BOT_GOOGLE: BotType = 'googlebot';

/**
 * 자동화·헤드리스·HTTP 클라이언트 UA(소문자 비교). ⚠️ DB public.cl_is_headless_ua 와 «같은 패턴» —
 * supabase/migrations/20261025_guard_3_pv_automation_reclass.sql 이 DB 쪽을 같은 값으로 맞춘다.
 */
export const AUTOMATION_UA_RE = /headless|playwright|puppeteer|phantomjs|selenium|webdriver|lighthouse|pagespeed|gtmetrix|python-requests|python-urllib|aiohttp|httpx|curl\/|wget|go-http-client|axios|node-fetch|undici|okhttp|java\/|libwww|scrapy/;

/** lane-A — bot 글자가 없는 알려진 수집기(소문자 비교). */
export const KNOWN_AGENT_RE = /ads-naver|bytespider|meta-externalagent|mediapartners-google/;

/**
 * lane-A — page_views 실측(10/26 · 30일 human 13,890행)에서 사람으로 적재된 «자동 방문 서명» 3종. 사람이 아니라 'other' 로 적는다.
 *   ① 'c109'       — Chrome/109.0.0.0 ∧ referrer 없음: 1,693 PV / 방문자 11(방문자당 150 PV · 사람 패턴 아님).
 *                    referrer 가 있으면 종전대로 사람(Windows 7/8.1 의 마지막 크롬이라 실제 사용자가 있다).
 *   ② 'ios_round'  — 「iPhone OS N_0」(소수점 아래 0 한 자리) ∧ referrer 없음: 693 PV / 476 · 「OS 15_0 + Version/18.0」 같은 위조 조합 포함.
 *   ③ 'ios_patch0' — 「OS N_M_0」: 실제 iOS 는 패치 0 을 적지 않는다(17_0_1 은 있어도 17_1_0 은 없다) → referrer 무관.
 * ⛔ 화면을 바꾸지 않는다(클로킹 금지) — 적재 분류에만 쓴다. ⛔ 넓히지 않는다: 위 셋 말고는 종전 판정 그대로.
 * DB 과거 행 재분류는 같은 세 식(sql 주석: supabase migration lanea_page_views_ua_sig_reclass).
 */
export type SuspiciousUaSig = 'c109' | 'ios_round' | 'ios_patch0';
const IOS_PATCH0_RE = /OS \d+_\d+_0 like Mac OS X/;
const IOS_ROUND_RE = /OS \d+_0 like Mac OS X/;
export function suspiciousUaSignature(userAgent: string | null | undefined, referrer: string | null | undefined): SuspiciousUaSig | null {
  const ua = String(userAgent ?? '');
  if (!ua) return null;
  if (IOS_PATCH0_RE.test(ua)) return 'ios_patch0';
  const noRef = !String(referrer ?? '').trim();
  if (!noRef) return null;
  if (ua.includes('Chrome/109.0.0.0')) return 'c109';
  if (IOS_ROUND_RE.test(ua)) return 'ios_round';
  return null;
}

export function classifyBot(userAgent: string | null | undefined): BotType {
  if (!userAgent) return 'human';
  const ua = userAgent.toLowerCase();
  // CL 0-A.3 (2026-10-15) — 헤드리스·자동화 브라우저(우리 측정 포함)는 사람이 아니다. DB 트리거
  //   trg_page_views_headless_reclass(public.cl_is_headless_ua)와 같은 패턴 — 둘 중 하나만 고치지 말 것.
  //   guard_1025 — HTTP 클라이언트 라이브러리(python-requests·curl·wget·Go·axios·node-fetch 등)도 사람이 아니다(AUTOMATION_UA_RE).
  if (AUTOMATION_UA_RE.test(ua)) return 'other';
  if (/yeti/.test(ua)) return 'yeti';
  if (/googlebot|google-inspectiontool|storebot-google/.test(ua)) return 'googlebot';
  if (/bingbot|bingpreview|msnbot/.test(ua)) return 'bingbot';
  if (/naverbot|naver-searchadvisor|navercorp/.test(ua)) return 'naver';
  if (/daumoa|daumcrawler|daumapp/.test(ua)) return 'daum';
  if (/bot|crawler|spider|slurp|applebot|facebot|twitterbot|linkedinbot|ahrefsbot|semrushbot|dotbot|petalbot|yandex|baiduspider|kakaotalk-scrap|seznambot/.test(ua)) return 'other';
  // lane-A — 이름에 bot 이 없는 알려진 수집기(네이버 광고 검수 Ads-Naver · Bytespider · Meta 수집 · 애드센스 수집).
  if (KNOWN_AGENT_RE.test(ua)) return 'other';
  return 'human';
}

/**
 * preempt2_measure b (instr/cl/resume_1155 §1 · 세션 A 승인 10/18) — 구글 referrer 위장 봇 «한 서명» 만.
 *   실측(page_views 30일): 이 UA 정확 일치 + referrer https://www.google.com/ 이 202건 — 10-03 첫 등장 · 매번 새 visitor_id ·
 *   전원 1PV · 같은 기간 GSC 클릭 0(구글 검색 유입이 실제로 없었다). 같은 UA 의 다른 referrer 는 direct 5건뿐.
 * ⛔ 넓히지 않는다: Linux 크롬 일반·다른 크롬 버전·다른 referrer 는 종전 판정 그대로(사람일 수 있다).
 *    UA 축약(Chrome/NNN.0.0.0) 때문에 같은 버전의 실제 Linux 사용자도 같은 UA 를 보낸다 → referrer 조건을 함께 건다.
 * 과거 행 재분류: sql/cl/measure/fake_google_ua_reclass.sql(+_rollback · 레일 SQL).
 */
export const FAKE_GOOGLE_REFERRAL_UA =
  'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/154.0.0.0 Safari/537.36';
const GOOGLE_SEARCH_REFERRER_RE = /^https?:\/\/(www\.)?google\.(com|co\.kr)\/?$/i;

/** page_views 적재용 — classifyBot + 위장 서명(UA 정확 일치 «그리고» 구글 검색 referrer) 하나. */
export function classifyPageView(userAgent: string | null | undefined, referrer: string | null | undefined): BotType {
  const base = classifyBot(userAgent);
  if (base !== 'human') return base;
  if (userAgent === FAKE_GOOGLE_REFERRAL_UA && GOOGLE_SEARCH_REFERRER_RE.test(String(referrer ?? '').trim())) return 'other';
  if (suspiciousUaSignature(userAgent, referrer)) return 'other'; // lane-A
  return base;
}
